import { useCallback } from 'react';

import { HeadlessShell } from '../PanelLayout.styles';
import { PanelLayoutLoader, PanelLayoutProvider, usePanelLayout } from '../PanelLayoutContext';
import { ThreeColumnPanel } from '../Panels/ThreeColumnPanel';

import type { ComponentProps, ReactNode } from 'react';

type HeadlessPanelLayoutProps = Omit<ComponentProps<typeof ThreeColumnPanel>, 'children'> & {
  children?: ReactNode;
  main: ReactNode;
};

const HeadlessLayoutContent = ({ main, ...props }: Omit<HeadlessPanelLayoutProps, 'children'>) => {
  const {
    hasLeftSidebar,
    hasRightSidebar,
    isLeftSidebarPadded,
    isMainContentPadded,
    isRightSidebarPadded,
    setSidebarPortalTargetLeft,
    setSidebarPortalTargetRight,
  } = usePanelLayout();

  const leftSidebarRef = useCallback(
    (el: HTMLDivElement | null) => setSidebarPortalTargetLeft(el),
    [setSidebarPortalTargetLeft],
  );

  const rightSidebarRef = useCallback(
    (el: HTMLDivElement | null) => setSidebarPortalTargetRight(el),
    [setSidebarPortalTargetRight],
  );

  return (
    <ThreeColumnPanel
      {...props}
      leftSidebar={props.leftSidebar ?? (hasLeftSidebar ? <div ref={leftSidebarRef} /> : undefined)}
      leftSidebarPadded={props.leftSidebarPadded ?? isLeftSidebarPadded}
      mainContentPadded={props.mainContentPadded ?? isMainContentPadded}
      rightSidebar={props.rightSidebar ?? (hasRightSidebar ? <div ref={rightSidebarRef} /> : undefined)}
      rightSidebarPadded={props.rightSidebarPadded ?? isRightSidebarPadded}
      showSidebarControls={true}
    >
      {main}
    </ThreeColumnPanel>
  );
};

export const HeadlessPanelLayout = ({ children, main, ...props }: HeadlessPanelLayoutProps) => (
  <PanelLayoutProvider>
    <HeadlessShell>
      {children}
      <HeadlessLayoutContent {...props} main={main} />
      <PanelLayoutLoader />
    </HeadlessShell>
  </PanelLayoutProvider>
);
